import { useEffect, useState } from 'react';
import { Loader2, Package, FileText } from 'lucide-react';
import { purchasingAPI, POLinePricebookResponse, POLinePricebookContract } from '../../lib/api';
import { usePreferences } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';
import { useColumnSort } from '../../hooks/useColumnSort';
import { SortableTh } from '../common/SortableTh';
import { descriptionHint } from './contractPartDescription';

interface POLinePricebookTabProps {
  lineId: number;
  /** Part number from the line — shown in the header so an empty result is self-explanatory. */
  partNumber?: string;
}

/**
 * CR-6411: Pricebooks tab on the PO Line Item pop-up.
 *
 * Shows the price bands held for this line's part number on LIVE contracts
 * between the line's customer and supplier — expired and draft contracts are
 * filtered out server-side. The table is a deliberate mirror of
 * Contract Details -> Parts (ContractPriceDetailModal, `parts` TabsContent):
 * same seven columns, same order, same raw price rendering
 * (`<currency> <price>`), same em-dash for empty values — so the two surfaces
 * never disagree. Rows are grouped by contract because a PO line's part can be
 * priced on more than one contract, and the band that PPV actually resolved for
 * this line is highlighted.
 *
 * @param lineId - PO line to look up pricing for
 * @param partNumber - the line's part number, for the header line
 * @link ContractPriceDetailModal for the Parts tab this mirrors
 */
/**
 * CR-7263: one contract's price bands, sortable.
 *
 * A child component rather than inline, because this table renders inside
 * `contracts.map()` and a hook cannot be called there. Each contract sorts
 * independently, which is also what a reader expects: they are comparing
 * bands within a contract, not across them.
 */
function ContractPartsTable({
  contract, matchedPartId,
}: { contract: POLinePricebookContract; matchedPartId?: number | null }) {
  const { sortedRows: sortedParts, sortField, sortDirection, onSort } = useColumnSort(
    contract.parts ?? [],
    { numericFields: ['qty_from', 'qty_to', 'unit_price', 'lot_price'] },
  );

  return (
    <>
          {/* Columns mirror Contract Details -> Parts exactly. */}
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <SortableTh field="part_number" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-3 py-2 font-medium">Part Number</SortableTh>
                  <SortableTh field="description" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-3 py-2 font-medium">Description</SortableTh>
                  <SortableTh field="qty_from" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right" className="text-right px-3 py-2 font-medium">Qty From</SortableTh>
                  <SortableTh field="qty_to" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right" className="text-right px-3 py-2 font-medium">Qty To</SortableTh>
                  <SortableTh field="unit_price" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right" className="text-right px-3 py-2 font-medium">Unit Price</SortableTh>
                  <SortableTh field="lot_price" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right" className="text-right px-3 py-2 font-medium">Lot Price</SortableTh>
                  <SortableTh field="unit_of_measure" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="left" className="text-left px-3 py-2 font-medium">UoM</SortableTh>
                </tr>
              </thead>
              <tbody>
                {sortedParts.map((p) => {
                  const isMatched = p.id != null && p.id === matchedPartId;
                  return (
                    <tr
                      key={p.id ?? `${p.part_number}-${p.qty_from}-${p.qty_to}`}
                      className={`border-t ${isMatched ? 'bg-emerald-50' : ''}`}
                    >
                      <td className="px-3 py-2 font-mono">
                        <span className="flex items-center gap-2">
                          {p.part_number}
                          {isMatched && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 whitespace-nowrap">
                              Applies to this line
                            </span>
                          )}
                        </span>
                      </td>
                      {/* Customer review 11 Sep (item 29): a blank band reads the part master's text. */}
                      <td className="px-3 py-2 whitespace-pre-line" title={descriptionHint(p.description_source)}>{p.description || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.qty_from}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.qty_to}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {p.unit_price ? `${contract.currency} ${p.unit_price}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {p.lot_price ? `${contract.currency} ${p.lot_price}` : '—'}
                      </td>
                      <td className="px-3 py-2">{p.unit_of_measure || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
    </>
  );
}

export default function POLinePricebookTab({ lineId, partNumber }: POLinePricebookTabProps) {
  const { formatDate } = usePreferences();
  const [data, setData] = useState<POLinePricebookResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPricebook = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await purchasingAPI.getLinePricebookParts(lineId);
        if (!cancelled) setData(response.data);
      } catch (err: unknown) {
        console.error('Failed to load pricebook parts:', err);
        if (!cancelled) {
          const axiosError = err as { response?: { data?: { message?: string } } };
          const message = axiosError.response?.data?.message || 'Failed to load pricebook information';
          setError(message);
          toast.error(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPricebook();
    return () => { cancelled = true; };
  }, [lineId]);

  // Same colours as the contract status badge in ContractPriceDetailModal, with
  // the pricebook vocabulary it uses in Purchasing > Pricing: active reads
  // "Live". Only live contracts reach this tab, so Draft/Expired are defensive.
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      draft: { label: 'Draft', className: 'bg-gray-100 text-gray-800' },
      active: { label: 'Live', className: 'bg-green-100 text-green-800' },
      expired: { label: 'Expired', className: 'bg-red-100 text-red-800' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  const contracts = data?.contracts ?? [];

  if (contracts.length === 0) {
    return (
      <div className="text-center py-12 bg-muted/30 rounded-lg">
        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">
          No live contract pricing found for {partNumber || data?.part_number || 'this part'}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          This part is not on a live contract between this customer and supplier.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">
          Live contract pricing for{' '}
          <span className="font-mono font-medium text-foreground">
            {partNumber || data?.part_number}
          </span>
          {data?.quantity && (
            <> · line qty <span className="tabular-nums font-medium text-foreground">{data.quantity}</span></>
          )}
        </p>
        {data && data.total_parts > 0 && (
          <p className="text-sm text-muted-foreground">
            {data.total_parts} price {data.total_parts === 1 ? 'band' : 'bands'} across{' '}
            {contracts.length} {contracts.length === 1 ? 'contract' : 'contracts'}
          </p>
        )}
      </div>

      {contracts.map((contract) => (
        <div key={contract.id}>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <FileText className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="font-mono font-semibold">{contract.contract_number}</span>
            {getStatusBadge(contract.status)}
            <span className="text-xs text-muted-foreground">
              {contract.valid_from ? formatDate(contract.valid_from) : '—'} –{' '}
              {contract.valid_to ? formatDate(contract.valid_to) : '—'}
            </span>
          </div>

          <ContractPartsTable contract={contract} matchedPartId={data?.matched_part_id} />
        </div>
      ))}
    </div>
  );
}
